import { useEffect, useState } from 'react';
import { IndexComponentsPanel } from '@/components/IndexComponentsStrip';
import {
  DEPOSIT_WITHDRAW_EXCHANGES,
  getDepositWithdrawStatusAll,
  type DepositWithdrawRow,
} from '@/services/depositWithdrawApi';
import './BasicInfoPanel.css';

type DepositLegResult = {
  exchange: string;
  label: string;
  rows: DepositWithdrawRow[];
  error: string | null;
};

const EXCHANGE_LABEL_MAP = Object.fromEntries(
  DEPOSIT_WITHDRAW_EXCHANGES.map(({ value, label }) => [value, label]),
) as Record<string, string>;

function toExchangeLabel(exchange: string): string {
  return EXCHANGE_LABEL_MAP[exchange] ?? exchange;
}

export interface BasicInfoPanelProps {
  symbol: string;
  exchange1: string;
  exchange2: string;
  instId1: string;
  instId2: string;
  indexComponents1: Record<string, unknown> | null | undefined;
  indexComponents2: Record<string, unknown> | null | undefined;
}

export function BasicInfoPanel({
  symbol,
  exchange1,
  exchange2,
  instId1,
  instId2,
  indexComponents1,
  indexComponents2,
}: BasicInfoPanelProps) {
  const [depositResults, setDepositResults] = useState<DepositLegResult[]>([]);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState<string | null>(null);

  useEffect(() => {
    if (!symbol.trim()) {
      setDepositResults([]);
      setDepositError(null);
      setDepositLoading(false);
      return;
    }

    let cancelled = false;
    setDepositLoading(true);
    setDepositError(null);

    void getDepositWithdrawStatusAll(symbol)
      .then((response) => {
        if (cancelled) return;
        const mapped = (response.results ?? []).map((item) => ({
          exchange: item.exchange,
          label: toExchangeLabel(item.exchange),
          rows: item.data ?? [],
          error: item.error,
        }));
        setDepositResults(mapped);
        if (mapped.length > 0 && mapped.every((item) => item.error)) {
          setDepositError('所有交易所查询都失败了，请检查代理服务和 API 凭证配置');
        }
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        setDepositResults([]);
        setDepositError(reason instanceof Error ? reason.message : String(reason));
      })
      .finally(() => {
        if (!cancelled) setDepositLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [symbol]);

  return (
    <div className="realtime-spread-basicinfo">
      <section className="realtime-spread-basicinfo-section">
        <h3 className="realtime-spread-basicinfo-title">Index 成分</h3>
        <IndexComponentsPanel
          exchange1={exchange1}
          exchange2={exchange2}
          instId1={instId1}
          instId2={instId2}
          indexComponents1={indexComponents1}
          indexComponents2={indexComponents2}
        />
      </section>

      <section className="realtime-spread-basicinfo-section">
        <h3 className="realtime-spread-basicinfo-title">充提状态</h3>
        {depositError ? <div className="realtime-spread-basicinfo-error">{depositError}</div> : null}
        {depositLoading ? (
          <div className="realtime-spread-basicinfo-empty">充提状态查询中…</div>
        ) : depositResults.length === 0 ? (
          <div className="realtime-spread-basicinfo-empty">暂无充提状态数据</div>
        ) : (
          <div className="realtime-spread-basicinfo-deposit-grid">
            {depositResults.map((result) => (
              <section key={result.exchange} className="realtime-spread-basicinfo-deposit-card">
                <h4>{result.label}</h4>
                {result.error ? (
                  <div className="realtime-spread-basicinfo-error">{result.error}</div>
                ) : result.rows.length === 0 ? (
                  <div className="realtime-spread-basicinfo-empty">无数据</div>
                ) : (
                  <div className="realtime-spread-basicinfo-table-wrap">
                    <table className="realtime-spread-basicinfo-table">
                      <thead>
                        <tr>
                          <th>链</th>
                          <th>充值</th>
                          <th>提现</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.rows.map((row) => (
                          <tr key={`${result.exchange}-${row.coin}-${row.chain}`}>
                            <td>{row.chain}</td>
                            <td className={row.deposit_enabled ? 'is-ok' : 'is-no'}>
                              {row.deposit_enabled ? '开放' : '关闭'}
                            </td>
                            <td className={row.withdraw_enabled ? 'is-ok' : 'is-no'}>
                              {row.withdraw_enabled ? '开放' : '关闭'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
